import GridLayout, { Layout } from 'react-grid-layout';
import { GridDashboardItem } from '../../types/insight-custom-dashboard';
import { GridItem, GridItemProps, UtrvHistoryModalProps } from './GridItem';
import { COLUMNS, GRID_WIDTH, MARGIN, ROW_HEIGHT, getLayoutByItems, isSDGContributionType, isSpaceType } from './utils';
import { useCallback, useRef, useImperativeHandle, useMemo } from 'react';
import { generateGridBackground } from './gridLayoutUtils';
import { useDashboardTableHeight } from '../../features/custom-dashboard/hooks/useDashboardTableHeight';
import './styles.scss';
import { useThemeMode } from '@features/theme/context/ThemeContext';

type GridLayoutDashboardProps = Pick<
  GridItemProps,
  | 'utrsData'
  | 'isEditing'
  | 'onItemRemove'
  | 'handleClickEdit'
  | 'handleClickDuplicate'
  | 'actionBtn'
  | 'integrationsData'
  | 'NoDataView'
  | 'onEditItem'
  | 'gridItemContext'
> & {
  // Layout props
  handleOpenUtrvHistoryModal?: (params: UtrvHistoryModalProps) => void;
  gridItems: GridDashboardItem[];
  onLayoutChange?: (layout: Layout[]) => void;
  onItemResize?: (newItemSize: Layout) => void;
  classes?: {
    container?: string;
  };
  dashboardRef?: React.ForwardedRef<HTMLDivElement>;
  pageBreaks?: number[];
};

const cellSize = (() => {
  const marginSlotsCount = COLUMNS - 1;
  const [horizontalMargin] = MARGIN;
  const totalHorizontalMargin = marginSlotsCount * horizontalMargin;
  const freeSpace = GRID_WIDTH - totalHorizontalMargin;
  return {
    width: freeSpace / COLUMNS,
    height: ROW_HEIGHT,
  };
})();

export const GridLayoutDashboard = (props: GridLayoutDashboardProps) => {
  const { isDarkMode } = useThemeMode();

  const {
    // Item-specific props
    isEditing,
    gridItems,
    utrsData,
    integrationsData,
    onItemRemove,
    handleClickEdit,
    handleClickDuplicate,
    actionBtn,
    onEditItem,
    // Context - passed directly from parent
    gridItemContext,
    // Layout props
    handleOpenUtrvHistoryModal = () => {},
    onLayoutChange,
    onItemResize,
    classes = { container: 'ps-3' },
    dashboardRef,
    pageBreaks,
  } = props;

  // Create internal ref and sync with forwarded ref to prevent undefined ref errors
  const internalRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(dashboardRef, () => internalRef.current as HTMLDivElement);

  const layout = getLayoutByItems(gridItems);

  const { viewLayout } = useDashboardTableHeight({
    gridItems,
    layout,
    dashboardRef: internalRef,
    utrsData,
  });

  const renderItem = useCallback(
    (item: GridDashboardItem) => (
      <div key={item._id}>
        <GridItem
          key={item._id}
          item={item}
          utrsData={utrsData}
          integrationsData={integrationsData}
          isEditing={isEditing}
          gridItemContext={gridItemContext}
          onItemRemove={onItemRemove}
          handleClickEdit={isSDGContributionType(item) || isSpaceType(item) ? undefined : handleClickEdit}
          handleClickDuplicate={handleClickDuplicate}
          handleOpenUtrvHistoryModal={handleOpenUtrvHistoryModal}
          NoDataView={props.NoDataView}
          actionBtn={actionBtn}
          onEditItem={onEditItem}
        />
      </div>
    ),
    [
      utrsData,
      integrationsData,
      isEditing,
      gridItemContext,
      onItemRemove,
      handleClickEdit,
      handleClickDuplicate,
      handleOpenUtrvHistoryModal,
      props.NoDataView,
      actionBtn,
      onEditItem,
    ],
  );

  const height = useMemo(() => {
    let lowestLayoutCellPoint = viewLayout.length > 0 ? Math.max(...viewLayout.map((l) => l.y + l.h)) : 0;
    if (isEditing) {
      lowestLayoutCellPoint += Math.ceil(window.innerHeight / cellSize.height);
    }
    const [_horizontalMargin, verticalMargin] = MARGIN;
    return (cellSize.height + verticalMargin) * lowestLayoutCellPoint;
  }, [viewLayout, isEditing]);

  const background = useMemo(
    () => (isEditing ? generateGridBackground({ cellSize, margin: MARGIN, cols: COLUMNS, gridWidth: GRID_WIDTH, darkMode: isDarkMode }) : ''),
    [isEditing, isDarkMode],
  );

  const style = useMemo(
    () => ({
      width: GRID_WIDTH,
      height,
      background: background,
    }),
    [height, background],
  );

  const children = useMemo(() => gridItems.map(renderItem), [gridItems, renderItem]);

  return (
    <div>
      <div
        className={`grid-container position-relative ${classes.container || ''}`}
        ref={internalRef}
        data-testid='grid-container'
        // 32px to represent the padding, must be in pixels as rem doesn't work
        style={{ width: `calc(${GRID_WIDTH}px + 32px)` }}
      >
        <GridLayout
          className='layout position-relative mb-5'
          containerPadding={[0, 0]}
          cols={COLUMNS}
          width={GRID_WIDTH}
          rowHeight={ROW_HEIGHT}
          margin={MARGIN}
          onResize={(currentLayout, oldItem, newItem) => onItemResize?.(newItem)}
          layout={viewLayout}
          isDraggable={isEditing}
          isResizable={isEditing}
          onLayoutChange={onLayoutChange}
          style={style}
        >
          {children}
        </GridLayout>
        {pageBreaks && pageBreaks.length
          ? pageBreaks.map((breakPoint, index) => (
              <div
                data-html2canvas-ignore
                className='ms-3'
                key={index}
                style={{
                  width: GRID_WIDTH,
                  position: 'absolute',
                  top: breakPoint,
                  left: 0,
                  right: 0,
                  borderTop: '2px dashed red',
                }}
              />
            ))
          : null}
      </div>
    </div>
  );
};
